import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ShareButtons } from "@/components/site/ShareButtons";
import { CommentSection } from "@/components/site/CommentSection";
import { ArrowLeft, Check } from "lucide-react";
import { COUNTIES } from "@/lib/constants";
import { toast } from "sonner";

export default function PetitieDetail() {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", county: "", message: "", public: true });
  const [signed, setSigned] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get(`/petitions/${slug}`).then(r => setP(r.data)).catch(() => setP(null)); }, [slug]);

  if (!p) return <div className="max-w-3xl mx-auto px-4 py-32 text-[#A0AABF]">Se încarcă...</div>;

  const pct = Math.min(100, (p.current_signatures / p.goal_signatures) * 100);

  const sign = async (e) => {
    e.preventDefault();
    if (form.name.trim().length < 2) { toast.error("Introdu numele tău"); return; }
    setLoading(true);
    try {
      const { data } = await api.post(`/petitions/${slug}/sign`, form);
      setP(data.petition);
      setSigned(true);
      toast.success("Mulțumim că ai semnat. Distribuie acum.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la semnare");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="page-petition-detail" className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20">
      <Link to="/petitii" className="inline-flex items-center gap-2 text-[#D4A63D] text-sm mb-6"><ArrowLeft className="w-4 h-4" /> Toate petițiile</Link>
      <span className="text-[10px] uppercase tracking-[0.3em] text-[#D4A63D]">{p.target_institution}</span>
      <h1 className="font-display text-3xl md:text-5xl text-[#F8F8F4] leading-tight mt-3">{p.title}</h1>

      <div className="cinematic-card rounded-lg p-5 mt-8 mb-10">
        <div className="flex items-center justify-between text-sm mb-2">
          <span className="text-[#D4A63D] font-medium text-base">{p.current_signatures.toLocaleString("ro-RO")} semnături</span>
          <span className="text-[#A0AABF]">obiectiv {p.goal_signatures.toLocaleString("ro-RO")}</span>
        </div>
        <div className="h-3 bg-[#0A1020] rounded-full overflow-hidden">
          <div className="h-full bg-gradient-to-r from-[#D4A63D] to-[#F2C053] transition-all" style={{ width: `${pct}%` }} />
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-8">
        <div className="md:col-span-2 space-y-8">
          <section>
            <h2 className="font-display text-2xl text-[#D4A63D] mb-3">Problema</h2>
            <p className="text-[#F8F8F4]/90 leading-relaxed">{p.problem_statement}</p>
          </section>
          <section>
            <h2 className="font-display text-2xl text-[#D4A63D] mb-3">Soluția propusă</h2>
            <p className="text-[#F8F8F4]/90 leading-relaxed">{p.solution_statement}</p>
          </section>
          <div className="cinematic-card rounded-lg p-5 flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-[#A0AABF]">Fiecare distribuire aduce mai aproape schimbarea.</p>
            <ShareButtons title={p.title} compact />
          </div>
        </div>

        <aside className="md:col-span-1">
          {signed ? (
            <div data-testid="petition-signed" className="cinematic-card rounded-lg p-6 text-center sticky top-24">
              <Check className="w-10 h-10 text-[#3BA76D] mx-auto mb-3" />
              <h3 className="font-display text-xl text-[#F8F8F4]">Ai semnat. Mulțumim.</h3>
              <p className="text-sm text-[#A0AABF] mt-2">Ajută-ne să ajungem mai departe.</p>
              <div className="mt-5"><ShareButtons title={p.title} /></div>
            </div>
          ) : (
            <form onSubmit={sign} className="cinematic-card rounded-lg p-5 sticky top-24 space-y-3">
              <h3 className="font-display text-lg text-[#F8F8F4]">Semnează petiția</h3>
              <input data-testid="pet-name" required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Numele tău *" className="w-full bg-[#0A1020] border border-white/10 rounded-sm px-3 py-2.5 text-sm" />
              <input data-testid="pet-email" type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="Email (opțional)" className="w-full bg-[#0A1020] border border-white/10 rounded-sm px-3 py-2.5 text-sm" />
              <select data-testid="pet-county" value={form.county} onChange={e => setForm({ ...form, county: e.target.value })} className="w-full bg-[#0A1020] border border-white/10 rounded-sm px-3 py-2.5 text-sm">
                <option value="">— Județ —</option>
                {COUNTIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              <textarea data-testid="pet-msg" value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} rows={3} placeholder="Mesaj (opțional)" className="w-full bg-[#0A1020] border border-white/10 rounded-sm px-3 py-2.5 text-sm" />
              <label className="text-xs text-[#A0AABF] flex gap-2 items-center"><input type="checkbox" checked={form.public} onChange={e => setForm({ ...form, public: e.target.checked })} className="accent-[#D4A63D]" /> Public ca semnatar</label>
              <button data-testid="pet-sign-btn" disabled={loading} className="btn-gold rounded-sm w-full py-3 text-sm disabled:opacity-50">{loading ? "Se semnează..." : "Semnează acum"}</button>
            </form>
          )}
        </aside>
      </div>

      <CommentSection targetType="petition" targetId={slug} />
    </div>
  );
}
